import type JCE from '@chesscom/js-chess-engine';
import { Game } from 'chessboard/model/game/types';
import data from 'src/utils/svelte/data';
import { isMovesRow } from '../config/types';
import type { MoveListComponent } from '../config/types';
import { getRowRenderKey } from './get-row-render-key';
import { isMatchingNode } from './is-matching-node';
import type { MoveListRowsStore } from '../store/types';

/**
 * Adds a mainline row without recreating the entire row list.
 * Should only be used to add the last node to the main line rows.
 * Modifies content of rows array
 */
export function addMainLineNodeToRows(node: JCE.Node.Model, moveListRows: MoveListRowsStore): void {
  let lastMainLineRow: MoveListComponent.Row<MoveListComponent.MoveListNode[]> | undefined;
  let lastMainLineRowIndex: number | undefined;

  const rows = moveListRows.get();

  // Iterate backwards to find the last mainline node and the one
  // before the node we're adding to make sure we don't add duplicates.
  // Assumes mainline nodes are in order and there are no gaps.
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i].get();
    if (isMovesRow(row) && row.depth === 0) {
      // if the move was already made, don't add the node again
      if (row.content.find((rowNode) => isMatchingNode(node, rowNode.node))) {
        return;
      }

      if (!lastMainLineRow) {
        lastMainLineRow = row;
        lastMainLineRowIndex = i;
      }

      // if we have iterated passed where the node we're adding would be,
      // we can stop searching for duplicates
      if (row.content[0].node.ids.move < node.ids.move) {
        break;
      }
    }
  }

  const lastMainLineRowIsFinalRow = lastMainLineRowIndex === rows.length - 1;
  if (
    node.color === Game.ColorsAsNumbers.Black &&
    lastMainLineRow !== undefined &&
    lastMainLineRowIndex !== undefined &&
    lastMainLineRowIsFinalRow
  ) {
    // we can just add the incoming black move if the last mainline row is the final row.
    rows[lastMainLineRowIndex].set({
      ...lastMainLineRow,
      content: [...lastMainLineRow.content, { node }],
    });
  } else {
    moveListRows.update((rowObjs) => {
      const rowBgColor = lastMainLineRow?.rowBgColor === 'light-row' ? 'dark-row' : 'light-row';

      const newRow: Omit<MoveListComponent.Row<MoveListComponent.MoveListNode[]>, 'renderKey'> = {
        depth: 0,
        content: [{ node }],
        rowBgColor,
      };

      rowObjs.push(data({ ...newRow, renderKey: getRowRenderKey(newRow) }));
      return rowObjs;
    });
  }
}
